/**
 * Pure helpers for Clamp quality control labor clock state (account menu hourglass, redirects).
 * Kept separate from `clampQcLaborApi.ts` so this module has no `fetch` or Vite-only `import.meta` usage
 * other than consumers passing flags in from the caller.
 */

/**
 * Stable `labor.device_session.page` value for a single Clamp QC job route.
 * One open session per user globally must not span two jobs, so keys must never collapse to `"clamp-qc"` for job workspaces.
 */
export function clampQcLaborPageKeyForJobSlug(slug: string): string {
  const t = slug.trim().toLowerCase().replace(/^\/+|\/+$/g, "").replace(/\s+/g, "-");
  return `clamp-qc-job:${t === "" ? "unknown" : t}`;
}

/**
 * Labor `page` query value that matches the current browser location for “am I clocked in here?” checks.
 * Job workspace routes use `clamp-qc-job:<slug>`; other Clamp quality control routes use `clamp-qc`.
 * Non–Clamp quality control routes use `clamp-qc` so an open job session surfaces as “elsewhere”.
 */
export function clampQcLaborExpectedPageFromPathname(pathname: string): string {
  const match = pathname.match(/^\/clamp-qc\/jobs\/([^/]+)/);
  if (match) {
    return clampQcLaborPageKeyForJobSlug(decodeURIComponent(match[1] ?? ""));
  }
  if (pathname.startsWith("/clamp-qc")) {
    return "clamp-qc";
  }
  return "clamp-qc";
}

export type LaborClockMenuState =
  | { kind: "none" }
  | { kind: "this_page"; locationLabel: string }
  | { kind: "other_page"; locationLabel: string };

/** Compares `labor.device_session.page` values case-insensitively; legacy Test Team aliases map to config. */
export function laborPageKeysEquivalent(left: string, right: string): boolean {
  return normalizeLaborPageKeyForEquivalence(left) === normalizeLaborPageKeyForEquivalence(right);
}

function normalizeLaborPageKeyForEquivalence(pageKey: string): string {
  const key = pageKey.trim().toLowerCase();
  if (key === "test team" || key === "test-team") {
    return "config";
  }
  return key;
}

/** Human-readable labor location for account menu copy (no abbreviations). */
export function formatLaborLocationLabel(pageKey: string): string {
  const trimmed = pageKey.trim();
  if (trimmed === "") {
    return "Unknown labor page";
  }
  if (trimmed.startsWith("clamp-qc-job:")) {
    const slug = trimmed.slice("clamp-qc-job:".length);
    const readable = slug === "" ? "unknown" : decodeURIComponent(slug);
    return `Clamp quality control job workspace (${readable})`;
  }
  if (trimmed === "clamp-qc") {
    return "Clamp quality control list or settings";
  }
  if (trimmed.startsWith("pre-pack-job:")) {
    const rest = trimmed.slice("pre-pack-job:".length);
    const parts = rest.split(":");
    const shipmentPart = parts[0]?.trim() ?? "";
    const cartonPart = parts[1]?.trim() ?? "";
    if (shipmentPart !== "" && cartonPart !== "") {
      return `Pre-Pack carton (shipment ${shipmentPart}, carton ${cartonPart})`;
    }
    return "Pre-Pack carton job";
  }
  if (trimmed === "pre-pack") {
    return "Pre-Pack queue";
  }
  if (trimmed.startsWith("config-job:")) {
    const rest = trimmed.slice("config-job:".length);
    const alternateMatch = /^alternate(?::(.+))?$/.exec(rest);
    if (alternateMatch) {
      const sku = alternateMatch[1]?.trim() ?? "";
      return sku === "" ? "Configuration inventory alternate" : `Configuration inventory alternate (${sku})`;
    }
    const batchItemId = rest.trim();
    if (batchItemId !== "" && /^\d+$/.test(batchItemId)) {
      return `Configuration build line (batch item ${batchItemId})`;
    }
    return "Configuration job";
  }
  if (trimmed.startsWith("assembly-job:")) {
    const buildOrderId = trimmed.slice("assembly-job:".length).trim();
    if (buildOrderId !== "") {
      return `Assembly work order ${buildOrderId}`;
    }
    return "Assembly work order";
  }
  if (trimmed === "assembly") {
    return "Assembly queue";
  }
  if (trimmed === "configuration") {
    return "Configuration queue";
  }
  if (trimmed.startsWith("transfer-pack-job:")) {
    const transferOrderId = trimmed.slice("transfer-pack-job:".length).trim();
    if (transferOrderId !== "") {
      return `Transfer pack (transfer order ${transferOrderId})`;
    }
    return "Transfer pack job";
  }
  if (trimmed === "final-qc") {
    return "Final quality control";
  }
  if (trimmed.toLowerCase() === "encapsulation") {
    return "Encapsulation";
  }
  if (trimmed.toLowerCase().startsWith("encapsulation-job:")) {
    const activity = trimmed.slice("encapsulation-job:".length).trim().toLowerCase();
    if (activity === "potting") return "Encapsulation (potting)";
    if (activity === "conformal-coat") return "Encapsulation (conformal coat)";
    return "Encapsulation";
  }
  if (trimmed.startsWith("kitting-pick:")) {
    const slotCount = trimmed
      .slice("kitting-pick:".length)
      .split(",")
      .map((part) => part.trim())
      .filter((part) => part.length > 0).length;
    if (slotCount > 1) {
      return `Picking (${String(slotCount)} jobs)`;
    }
    return "Picking";
  }
  if (trimmed.startsWith("kitting-pick-run:")) {
    const runId = trimmed.slice("kitting-pick-run:".length).trim();
    return runId === "" ? "Picking" : `Picking (run ${runId})`;
  }
  if (trimmed === "kitting") {
    return "Picking queue";
  }
  if (trimmed === "config" || trimmed.toLowerCase() === "test team" || trimmed.toLowerCase() === "test-team") {
    return "Config";
  }
  if (trimmed === "hub-overview") {
    return "Elsewhere on the Integrator";
  }
  if (trimmed.startsWith("receiving-job:")) {
    const rest = trimmed.slice("receiving-job:".length).trim();
    if (rest === "arrivals") return "Receiving arrivals";
    if (rest === "receive") return "Receive queue";
    if (rest === "holding") return "Receiving holding";
    if (rest === "to-stock") return "Receiving to stock";
    const receiveMatch = /^receive:(purchase_order|transfer_order):(\d+)$/.exec(rest);
    if (receiveMatch) {
      const kind = receiveMatch[1] === "purchase_order" ? "purchase order" : "transfer order";
      return `Receive (${kind} ${receiveMatch[2]})`;
    }
    return "Receiving";
  }
  if (trimmed === "receiving") {
    return "Receiving";
  }
  if (trimmed === "Receiving") {
    return "Receiving (mobile app)";
  }
  if (trimmed === "Transfers") {
    return "Transfers (mobile app)";
  }
  if (trimmed === "Orders") {
    return "Misc / Orders (mobile app)";
  }
  return `Labor page ${trimmed}`;
}

/**
 * True when the active-session response means the operator has an open `labor.device_session` whose `page` differs
 * from the `page` query used for the request (for example clocked in on another app page while viewing the hub list).
 */
export function clampQcLaborPayloadSignalsActiveOnOtherPage(
  payload: Record<string, unknown>,
  expectedLaborPage: string = "clamp-qc",
): boolean {
  const otherRaw = payload.active_on_other_page;
  const otherPage = typeof otherRaw === "string" ? otherRaw.trim() : "";
  if (otherPage === "") {
    return false;
  }
  return !laborPageKeysEquivalent(otherPage, expectedLaborPage);
}

const INSPECTION_LIST_LABOR_PAGE_KEY = "clamp-qc";

/**
 * Inspection list uses {@link INSPECTION_LIST_LABOR_PAGE_KEY} for active-session queries, so an open job workspace
 * appears as labor on “another” page. Disable “Open job” only when that active job is not the row’s job, so operators
 * can return to the work order they are clocked into.
 */
export function clampQcInspectionListShouldDisableOpenJobForSlug(input: {
  laborGatePending: boolean;
  activeSessionPayload: Record<string, unknown> | undefined;
  jobSlug: string;
}): boolean {
  const { laborGatePending, activeSessionPayload, jobSlug } = input;
  if (laborGatePending) {
    return true;
  }
  if (!activeSessionPayload) {
    return false;
  }
  if (!clampQcLaborPayloadSignalsActiveOnOtherPage(activeSessionPayload, INSPECTION_LIST_LABOR_PAGE_KEY)) {
    return false;
  }
  const otherRaw = activeSessionPayload.active_on_other_page;
  const otherPage = typeof otherRaw === "string" ? otherRaw.trim() : "";
  if (otherPage === "") {
    return true;
  }
  return !laborPageKeysEquivalent(otherPage, clampQcLaborPageKeyForJobSlug(jobSlug));
}

function laborSessionPageFromRow(session: Record<string, unknown>): string {
  const raw = session.page;
  if (typeof raw === "string") {
    return raw.trim();
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(raw);
  }
  if (raw != null && typeof raw !== "object") {
    const text = String(raw).trim();
    return text;
  }
  return "";
}

/** Interprets labor active-session payloads (for example `GET /api/hub/labor/active-session` or Clamp QC job fetches) for header badge and menu copy. */
export function laborClockMenuStateFromActiveSessionPayload(
  payload: Record<string, unknown>,
  expectedLaborPage: string,
): LaborClockMenuState {
  const expected = expectedLaborPage.trim();
  const otherRaw = payload.active_on_other_page;
  const otherPage = typeof otherRaw === "string" ? otherRaw.trim() : "";
  if (otherPage !== "" && !laborPageKeysEquivalent(otherPage, expected)) {
    return { kind: "other_page", locationLabel: formatLaborLocationLabel(otherPage) };
  }
  const sessionRaw = payload.session;
  if (sessionRaw && typeof sessionRaw === "object" && !Array.isArray(sessionRaw)) {
    const session = sessionRaw as Record<string, unknown>;
    const page = laborSessionPageFromRow(session);
    if (page !== "") {
      if (laborPageKeysEquivalent(page, expected)) {
        return { kind: "this_page", locationLabel: formatLaborLocationLabel(page) };
      }
      return { kind: "other_page", locationLabel: formatLaborLocationLabel(page) };
    }
  }
  return { kind: "none" };
}

/**
 * Derives account-menu labor clock state from the active-session query plus whether the job workspace skips
 * Supabase labor persistence (default Vite dev without `VITE_CLAMP_QC_USE_SUPABASE_LABOR=true`).
 *
 * In that mode the job page does not create a `labor.device_session` row, so the API returns `{ kind: "none" }`
 * while the operator is still “on the clock” for timers. Treat a successful empty payload on a `clamp-qc-job:*`
 * expected page as clocked in here so the green hourglass matches the workspace.
 */
export function effectiveLaborClockMenuStateForUserMenu(input: {
  queryIsSuccess: boolean;
  payload: Record<string, unknown> | undefined;
  expectedLaborPage: string;
  remotePersistenceDisabled: boolean;
}): LaborClockMenuState {
  const { queryIsSuccess, payload, expectedLaborPage, remotePersistenceDisabled } = input;
  if (!queryIsSuccess || !payload) {
    return { kind: "none" };
  }
  const fromApi = laborClockMenuStateFromActiveSessionPayload(payload, expectedLaborPage);
  const trimmedExpected = expectedLaborPage.trim().toLowerCase();
  const onClampQcJobRoute = trimmedExpected.startsWith("clamp-qc-job:");
  const onPrePackCartonRoute = trimmedExpected.startsWith("pre-pack-job:");
  const onJobWorkspaceRoute = onClampQcJobRoute || onPrePackCartonRoute;
  if (fromApi.kind === "none" && remotePersistenceDisabled && onJobWorkspaceRoute) {
    return {
      kind: "this_page",
      locationLabel: formatLaborLocationLabel(expectedLaborPage),
    };
  }
  return fromApi;
}
